import {Action, Actions} from 'platform/components';
import {Icon, Link, Space, Stack} from 'platform/foundation';

import {any, compose, not, values} from 'ramda';
import {isFalsy, isTrue} from 'ramda-adjunct';

import {FileResponseBody} from '@dms/api/shared';
import i18n from '@dms/i18n';

import {TestIdProps, buildArray, openFile, suffixTestId} from 'shared/web';

import {useEmailConfig} from '../../../hooks/useEmailConfig';
import {usePermissions} from '../../../hooks/usePermissions/usePermissions';
import {downloadHostedFile} from '../../../utils/downloadHostedFile';
import {getIconKeyByFileName} from '../../../utils/getIconKeyByFileName';
import {hasAclDocsWithPermission} from '../../../utils/hasAclDocsWithPermission';
import {printPDF} from '../../../utils/printPDF';

interface DocumentItemProps extends TestIdProps {
  document: FileResponseBody;
  onSendEmail: (document: FileResponseBody) => void;
  isDocxFormat?: boolean;
  permissions?: {[key: string]: boolean};
}

export function DocumentItem(props: DocumentItemProps) {
  const {isEnabled: isEmailConfigEnabled} = useEmailConfig();

  const [canReadDocuments] = usePermissions({
    permissionKeys: ['readDocuments'],
  });

  const openDocument = () => {
    openFile(props.document.pdfUri);
  };

  const printDocument = () => {
    printPDF(props.document.pdfUri);
  };

  const isActionDisabled = any(
    compose(not, hasAclDocsWithPermission),
    values(props.permissions ?? {})
  );

  const isLinkDisabled = !canReadDocuments || isActionDisabled;

  const downloadDocument = () => {
    if (props.isDocxFormat) {
      downloadHostedFile(props.document.fileUri, props.document.filename);
    } else {
      downloadHostedFile(props.document.pdfUri, props.document.pdfFilename);
    }
  };

  const emailDocument = () => {
    props.onSendEmail(props.document);
  };

  const actions = buildArray<Action>()
    .when(canReadDocuments, {
      type: 'iconButton',
      icon: 'action/print',
      onClick: printDocument,
      isDisabled: isLinkDisabled,
    })
    .when(canReadDocuments, {
      type: 'iconButton',
      icon: 'file/download',
      'data-testid': suffixTestId('download', props),
      onClick: downloadDocument,
      isDisabled: isLinkDisabled,
    })
    .when(canReadDocuments && isTrue(isEmailConfigEnabled), {
      type: 'iconButton',
      icon: 'communication/email',
      onClick: emailDocument,
      isDisabled: isLinkDisabled,
    })
    .when(canReadDocuments && isFalsy(isEmailConfigEnabled), {
      type: 'iconButtonWithTooltip',
      icon: 'communication/email',
      tooltip: {label: i18n.t('general.notifications.setupEmailProvider')},
      onClick: emailDocument,
      isDisabled: isLinkDisabled,
    });

  return (
    <Stack direction={['column', 'row']} spacing={[2, 4]} align="center">
      <Icon value={getIconKeyByFileName(props.document.filename)} />
      <Link
        title={props.document.filename}
        isDisabled={isLinkDisabled}
        size="xSmall"
        onClick={openDocument}
        data-testid={suffixTestId('documentItemLink', props)}
      />
      <Space fillAvailable />
      <Actions actions={actions} data-testid={suffixTestId('documentItemActions', props)} />
    </Stack>
  );
}
